import { grey } from "@mui/material/colors";
import Popover from "@mui/material/Popover";
import Stack from "@mui/material/Stack";
import type { SxProps } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import React from "react";
import type { StaffMember } from "services/types/staff.type";
import { getFullName } from "utils/common";
import FieldLabel from "./field-label.component";

type StaffTaskAssigneeDisplayProps = {
  assignees: Partial<StaffMember>[];
  isComplete?: boolean;
  textSx?: SxProps;
  sx?: SxProps;
};

const getAssigneeFullName = (assignee: Partial<StaffMember>) => {
  return getFullName({
    firstName: assignee?.first_name ?? "",
    lastName: assignee?.last_name ?? "",
    title: assignee?.title,
    suffix: assignee?.suffix ?? "",
  });
};

export default function StaffTaskAssigneeDisplay({
  assignees,
  isComplete,
  textSx,
  sx,
}: StaffTaskAssigneeDisplayProps) {
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);

  const handlePopoverOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  const [assignee, ...additionalAssignees] = assignees;

  const assigneeFullName = getAssigneeFullName(assignee);

  const additionalCountDisplay = additionalAssignees.length
    ? `${additionalAssignees.length > 99 ? "99" : additionalAssignees.length}`
    : "";

  const open = Boolean(additionalCountDisplay && anchorEl);

  return (
    <Stack
      direction="row"
      alignItems="center"
      columnGap={1}
      sx={{ cursor: additionalCountDisplay ? "pointer" : undefined, ...sx }}
      onMouseEnter={handlePopoverOpen}
      onMouseLeave={handlePopoverClose}
    >
      <Typography
        variant="body2"
        sx={{ color: isComplete ? grey[700] : undefined, ...textSx }}
      >
        {assigneeFullName}
      </Typography>
      {additionalCountDisplay && (
        <Stack>
          <Typography
            component="div"
            variant="body2"
            width="26px"
            height="26px"
            alignItems="center"
            justifyContent="center"
            display="flex"
            sx={{
              fontWeight: 700,
              fontSize: "14px",
              cursor: "pointer",
              background: grey[500],
              color: "white",
              borderRadius: "50%",
            }}
          >
            +{additionalCountDisplay}
          </Typography>
        </Stack>
      )}
      <Popover
        id="mouse-over-popover"
        sx={{ pointerEvents: "none", zIndex: 1500 }}
        open={open}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        onClose={handlePopoverClose}
        disableRestoreFocus
      >
        <Stack p={2} width="100%">
          <FieldLabel label="Additional Assignees" sx={{ mb: 2 }} />
          <Stack direction="column">
            {additionalAssignees.map((assignee) => (
              <Typography
                component="div"
                key={assignee.id}
                variant="body2"
                pb={1.5}
              >
                {getAssigneeFullName(assignee)}
              </Typography>
            ))}
          </Stack>
        </Stack>
      </Popover>
    </Stack>
  );
}
